import type {DiscordContext, DiscordParticipant} from './discord';

const SEAT_COLORS = ['#ff6b6b', '#4dd0ff', '#9cff7a', '#ffd166'];

export type LobbyHandlers = {
  onQueue: () => void;
  onCancel: () => void;
  onInvite?: () => void;
};

export class LobbyPanel {
  private root: HTMLElement;
  private title: HTMLElement;
  private subtitle: HTMLElement;
  private chips: HTMLElement;
  private actions: HTMLElement;
  private statusEl: HTMLElement;
  private queued = false;

  constructor(rootSelector: string, private handlers: LobbyHandlers) {
    const root = document.querySelector<HTMLElement>(rootSelector);
    if (!root) throw new Error(`LobbyPanel: missing ${rootSelector}`);
    this.root = root;
    this.title = required(root, '[data-lobby-title]');
    this.subtitle = required(root, '[data-lobby-subtitle]');
    this.chips = required(root, '[data-lobby-chips]');
    this.actions = required(root, '[data-lobby-actions]');
    this.statusEl = required(root, '[data-lobby-status]');
  }

  update(ctx: DiscordContext): void {
    const inCall = ctx.inCall;
    const count = ctx.participants.length || 1;
    if (inCall) {
      this.title.textContent = count > 1 ? `Sharing the room with ${count} players` : 'In a Discord call · waiting for friends';
      this.subtitle.textContent = `Channel ${shortId(ctx.channelId ?? 'unknown')} · instance ${shortId(ctx.instanceId)}`;
      this.root.dataset.mode = 'call';
    } else {
      this.title.textContent = 'Solo mode';
      this.subtitle.textContent = 'Launch from a voice/group call to play together, or queue for a public match.';
      this.root.dataset.mode = 'solo';
    }
    this.renderChips(ctx.participants);
    this.renderActions(ctx);
  }

  setStatus(msg: string): void {
    this.statusEl.textContent = msg;
  }

  matched(): void {
    this.queued = false;
    this.renderActionsState();
  }

  private renderChips(participants: DiscordParticipant[]): void {
    this.chips.innerHTML = '';
    participants.forEach((p, i) => {
      const chip = document.createElement('span');
      chip.className = 'lobby-chip';
      const color = SEAT_COLORS[i] ?? '#cfd8dc';
      chip.style.borderColor = color;
      chip.style.color = color;
      chip.textContent = `P${i + 1} · ${p.globalName ?? p.username}`;
      this.chips.appendChild(chip);
    });
  }

  private renderActions(ctx: DiscordContext): void {
    this.actions.innerHTML = '';
    if (ctx.inCall) {
      const note = document.createElement('span');
      note.className = 'lobby-note';
      note.textContent = 'Auto-joined this Discord activity instance.';
      this.actions.appendChild(note);
      return;
    }

    const queueBtn = document.createElement('button');
    queueBtn.type = 'button';
    queueBtn.className = 'lobby-button';
    queueBtn.dataset.role = 'queue';
    queueBtn.addEventListener('click', () => {
      if (this.queued) {
        this.queued = false;
        this.handlers.onCancel();
      } else {
        this.queued = true;
        this.handlers.onQueue();
      }
      this.renderActionsState();
    });
    this.actions.appendChild(queueBtn);

    if (this.handlers.onInvite) {
      const inviteBtn = document.createElement('button');
      inviteBtn.type = 'button';
      inviteBtn.className = 'lobby-button secondary';
      inviteBtn.textContent = 'Invite a friend';
      inviteBtn.addEventListener('click', () => this.handlers.onInvite!());
      this.actions.appendChild(inviteBtn);
    }

    this.renderActionsState();
  }

  private renderActionsState(): void {
    const queueBtn = this.actions.querySelector<HTMLButtonElement>('[data-role="queue"]');
    if (queueBtn) {
      queueBtn.textContent = this.queued ? 'Cancel matchmaking' : 'Find a match';
      queueBtn.classList.toggle('active', this.queued);
    }
  }
}

function required<T extends HTMLElement>(root: HTMLElement, selector: string): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`LobbyPanel: ${selector} not found`);
  return el;
}

function shortId(id: string): string {
  return id.length > 14 ? id.slice(0, 12) + '…' : id;
}
